import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui-kit/collapsible';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { cn } from '@/lib/utils';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui-kit/form';
import { FieldLabel } from '../required-fields';
import { EMPLOYMENT_TYPES } from '@/constant/hr/employment-types';
import { HrInfoValues } from '../../../schemas/employee-form-schema';
import { withDefaultLeave } from '../../../schemas/hr-term-schema';
import { BOXED_CHECKBOX_LABEL, FIELD_GRID, FormSection, HISTORY_YEARS } from '../form-layout';
import { LeaveAllotmentSection } from './leave-allotment-section';
import { WorkingDaysSection } from './working-days-section';

export interface HrTermCardProps {
  index: number;
  isActive: boolean;
  canActivate: boolean;
  canRemove: boolean;
  onActivate: () => void;
  onRemove: () => void;
  /** Whether the card starts expanded (the active term and terms added in this session do). */
  defaultOpen?: boolean;
}

/**
 * One HR term, as a collapsible box: its period, employment type, its working
 * days and its leave allotment / carry-forwards, plus the activate / remove actions. A collapsed term
 * that fails validation opens itself so the error can be seen.
 */
export const HrTermCard = ({
  index,
  isActive,
  canActivate,
  canRemove,
  onActivate,
  onRemove,
  defaultOpen = false,
}: HrTermCardProps) => {
  const { t } = useTranslation();
  const { control, watch, getValues, setValue, formState } = useFormContext<HrInfoValues>();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const startDate = watch(`HrTerms.${index}.HrPeriodStartDate`);
  const endDate = watch(`HrTerms.${index}.HrPeriodEndDate`);
  const employmentType = watch(`HrTerms.${index}.EmploymentType`);

  const hasErrors = Boolean(formState.errors.HrTerms?.[index]);
  useEffect(() => {
    if (hasErrors) setIsOpen(true);
  }, [hasErrors, formState.submitCount]);

  const summary = [
    startDate && endDate ? `${formatDateOnly(startDate)} - ${formatDateOnly(endDate)}` : undefined,
    employmentType ? t(employmentType) : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  /** A new employment type suggests its own default leave (casual for an intern or probationer). */
  const handleEmploymentTypeChange = (next: string, onChange: (value: string) => void) => {
    onChange(next);
    setValue(
      `HrTerms.${index}.LeaveDetails`,
      withDefaultLeave(getValues(`HrTerms.${index}.LeaveDetails`), next, true),
      { shouldDirty: true }
    );
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn('overflow-hidden rounded-lg border', hasErrors && 'border-destructive')}
    >
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-2 bg-muted/40 px-4 py-3',
          isOpen && 'border-b'
        )}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ChevronDown
              className={cn('size-4 shrink-0 transition-transform', !isOpen && '-rotate-90')}
            />
            <span className="text-sm font-semibold">
              {t('HR_TERM')} {index + 1}
            </span>
            {summary && <span className="truncate text-xs text-muted-foreground">{summary}</span>}
          </button>
        </CollapsibleTrigger>
        <div className="flex items-center gap-2">
          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onRemove}
              aria-label={`${t('DELETE')} ${t('HR_TERM')} ${index + 1}`}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant={isActive ? 'default' : 'outline'}
            disabled={isActive || !canActivate}
            onClick={onActivate}
          >
            {isActive ? t('ACTIVATED') : t('ACTIVE')}
          </Button>
        </div>
      </div>

      {/* Kept mounted while collapsed so every term's fields stay registered and validate. */}
      <CollapsibleContent
        forceMount
        className="flex flex-col gap-6 p-4 data-[state=closed]:hidden md:p-5"
      >
        <FormSection title={t('HR_PERIOD')}>
          <div className={FIELD_GRID}>
            <FormField
              control={control}
              name={`HrTerms.${index}.HrPeriodStartDate`}
              render={({ field }) => (
                <FormItem>
                  <FieldLabel>{t('START_DATE')}</FieldLabel>
                  <FormControl>
                    <DatePicker
                      placeholder={t('SELECT_DATE')}
                      max={endDate || undefined}
                      yearsBack={HISTORY_YEARS}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name={`HrTerms.${index}.HrPeriodEndDate`}
              render={({ field }) => (
                <FormItem>
                  <FieldLabel>{t('END_DATE')}</FieldLabel>
                  <FormControl>
                    <DatePicker
                      placeholder={t('SELECT_DATE')}
                      min={startDate || undefined}
                      disabled={!startDate}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <FormSection title={t('EMPLOYMENT_TYPE')} required>
          <div className={FIELD_GRID}>
            <FormField
              control={control}
              name={`HrTerms.${index}.EmploymentType`}
              render={({ field }) => (
                <FormItem>
                  <Select
                    onValueChange={(next) => handleEmploymentTypeChange(next, field.onChange)}
                    value={field.value || undefined}
                  >
                    <FormControl>
                      <SelectTrigger aria-label={t('EMPLOYMENT_TYPE')}>
                        <SelectValue placeholder={t('SELECT_TYPE')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {EMPLOYMENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {t(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name={`HrTerms.${index}.IsFlexible`}
              render={({ field }) => (
                <FormItem>
                  <label className={BOXED_CHECKBOX_LABEL}>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                    {t('FLEXIBLE')}
                  </label>
                </FormItem>
              )}
            />
          </div>
        </FormSection>

        <WorkingDaysSection termIndex={index} />
        <LeaveAllotmentSection termIndex={index} />
      </CollapsibleContent>
    </Collapsible>
  );
};
